import React, { useEffect, useState } from 'react';
import Axios from 'axios';

import AsyncImage from '@appComponents/AsyncImage/AsyncImage';
import WhitePaper from '@appComponents/WhitePaper/WhitePaper';
import ZebraDetailsCard from '@appComponents/ZebraDetailsCard/ZebraDetailsCard';
import CurrencyElement from '@appComponents/CurrencyElement/CurrencyElement';

import { IProcessedInvestment, IProcessedPitchInvestmentsGroup } from './IProcessedInvestments';
import { formatDate, toUpper } from '@features/utils/functions';
import { classNames as cn } from '@features/utils/reactFunctions';

import * as style from './ProcessedInvestmentsStyle.module.scss'
import * as elements from '@appStyles/_elements.module.scss';
import paymentInfos from './paymentInfos';
import Notice from '@appComponents/Notice/Notice';

import noImageSmall from '@images/no_image_small.png'

const labels = {
  id: 'ID',
  currency: 'Currency',
  amount: 'Amount',
  createdAt: 'Date',
  status: 'Status',
  investments: 'Investment',
  balanceParticipation: 'Top up',
  info: 'Info',
  page: 'Pitch',
};

const ProcessedInvestmentGroup = ({ invGroup } : { invGroup: IProcessedPitchInvestmentsGroup }) => {
  const [expanded, setExpanded] = useState(false);
  const iconClass = expanded ? 'fa fa-angle-down' : 'fa fa-angle-right';

  const formatValue = (label: string, value: string, parent: string, currency: string):
  string | number | boolean | JSX.Element => {
    switch (label) {
      case 'Top up':
        return 'Used';
      case 'Currency':
        return value.toString().toUpperCase();
      case 'Status':
        return typeof value === 'string' && toUpper(value);
      case 'Date':
        return typeof value === 'string' && formatDate(value);
      case 'Amount':
        return (
          <CurrencyElement
            value={value as string}
            currency={currency}
            withCurrencyLabel={false}
          />);
      case 'Info':
        return paymentInfos[value] || value;
      case 'Pitch':
        const [expired, slug] = value.split('_');
        return (
          <a
            target="_blank"
            href={`/pitches/${slug}`}
            className={style.pitchLink}
          >
            {expired === 'true' ? 'View page' : 'Live opportunity'}
          </a>
        );
      default:
        return value;
    }
  };

  return (
    <div
      className={cn(elements.zebraTableRow, style.zebraTableRow)}
      onClick={() => setExpanded(!expanded)}
    >
      <div className={cn(elements.zebraTableItem, style.zebraTableItemFirst)}>
        <AsyncImage image={invGroup.pitch.avatar} fallback={noImageSmall}>
          <div className={style.avatar} />
        </AsyncImage>
      </div>
      <div className={cn(elements.zebraTableItem, style.zebraTableItemLast)}>
        {invGroup.pitch.title}
        <div
          className={cn(elements.arrowIcon, iconClass, style.dragRight)}
          title="More Details"
        />
      </div>
      {expanded && (
        <ZebraDetailsCard
          formatValue={formatValue}
          data={{
            page: invGroup.pitch.page,
            investments: invGroup.investments,
          }}
          labels={labels}
        />
      )}
    </div>
  );
};

const ProcessedInvestments = () => {
  const [data, setData] = useState([]);

  useEffect(() => {
    Axios.get('/api/portfolio/current_investor/live_pitches/investments').then((resp) => {
      const { data } = resp;

      setData(data.reduce((tally: IProcessedPitchInvestmentsGroup[], inv: IProcessedInvestment) => {
        const itemIndex = tally.findIndex(el => el && el.pitch && el.pitch.id === inv.pitch.id);
        const { id, amount, currency, balance_participation, payment_method, created_at, status } = inv;

        const investment = {
          id,
          amount,
          currency,
          status,
          createdAt: created_at,
          balanceParticipation: balance_participation,
          info: `${payment_method}_${status}`,
        };

        if (itemIndex > -1) {
          tally[itemIndex].investments.push(investment);
        } else {
          const { slug, expired, ...pitch } = inv.pitch;
          tally.push({
            pitch: {
              ...pitch,
              page: `${expired}_${slug}`,
            },
            investments: [investment],
          });
        }

        return tally;
      }, []));
    });
  }, []);

  return data.length > 0 ? (
    <WhitePaper headline={'Live Investment Transactions'} className={style.main}>
      <div className={cn(elements.zebraTableContainer, style.zebraTableContainer)}>
        {data.map((el: IProcessedPitchInvestmentsGroup) => (
          <ProcessedInvestmentGroup key={`${el.pitch.id}_pitch`} invGroup={el} />
        ))}
      </div>
      <Notice>
        Above is a list of all transactions related to pitches that have not closed yet. If you initiated more
        than one investment you can check the status of each payment above. Once all legal work is complete this
        transaction list will be updated.
      </Notice>
    </WhitePaper>
  ) : null;
};

export default ProcessedInvestments;
